import { submitRequestWithPromise } from './BaseServiceTs';
import { GET, POST, PUT } from './RequestMethods';
import { environment } from './environment';
import { AuctionDetailsPayload, AuctionFormValues } from '../actions/AuctionActions';
import moment from 'moment';

interface fmGetAuctionParams {
    auction_id?: number | undefined;
    draft_id?: number | undefined;
    import_id?: number | undefined;
    relist_id?: number | undefined;
    auction_status?: string;
    scope?: string | boolean | undefined;
    facility_id?: number | undefined;
    import_unit_id?: number | undefined;
    has_public_notice?: boolean;
}

interface AuctionResponse {
    auction: {
        upcoming?: boolean
        facility_id?: number
        total_bids?: string
    };
}

const AuctionService = {
    async fmGetAuction({
        auction_id = undefined,
        draft_id = undefined,
        import_id = undefined,
        relist_id = undefined,
        auction_status = 'active',
        scope = false,
        facility_id = undefined,
        import_unit_id = undefined,
        has_public_notice = false,
    }: fmGetAuctionParams): Promise<AuctionResponse> {
        let params: Record<string, any> = {};

        if (has_public_notice) {
            params.has_public_notice = true;
        }

        if (facility_id) {
            params.facility_id = facility_id;
        }

        if (import_unit_id) {
            params.import_unit_id = import_unit_id;
        }

        if (scope) {
            params.scope = scope;
        }

        if (auction_id) {
            return submitRequestWithPromise(
                GET,
                environment.apiEndPoint,
                `users/account/auctions/${auction_status}/${auction_id}`,
                params,
            );
        } else if (relist_id) {
            return submitRequestWithPromise(
                GET,
                environment.apiEndPoint,
                `users/account/auctions/unsold/${relist_id}`,
                params,
            );
        } else if (draft_id) {
            return submitRequestWithPromise(
                GET,
                environment.apiEndPoint,
                `users/account/auctions/drafts/${draft_id}`,
                params,
            );
        } else if (import_id) {
            return submitRequestWithPromise(
                GET,
                environment.apiEndPoint,
                `users/account/auctions/imports/${import_id}`,
                params,
            );
        }

        return new Promise((resolve, reject) => {
            reject('missing auction or draft id');
        });
    },

    async saveAuctionDetails(form_details: AuctionFormValues): Promise<AuctionDetailsPayload> {
        const {
            draft_id = false,
            auction_id = false,
            import_id = false,
            relist_id = false,
            ...otherDetails
        } = form_details;

        const updatedDetails = {
            ...otherDetails,
            active_date: moment(otherDetails.active_date).format('MM/DD/YYYY'),
            expire_date: moment(otherDetails.expire_date).format('MM/DD/YYYY'),
        };

        if (draft_id) {
            return submitRequestWithPromise(
                PUT,
                environment.apiEndPoint,
                `users/account/auctions/drafts/${draft_id}`,
                { auction_draft: updatedDetails },
            );
        } else if (auction_id) {
            return submitRequestWithPromise(
                PUT,
                environment.apiEndPoint,
                `users/account/auctions/active/${auction_id}`,
                { auction: { ...updatedDetails, auction_id } },
            );
        } else if (relist_id) {
            return submitRequestWithPromise(
                POST,
                environment.apiEndPoint,
                'users/account/auctions/drafts',
                { auction_draft: { ...updatedDetails, relist_id } },
            );
        } else if (import_id) {
            return submitRequestWithPromise(
                POST,
                environment.apiEndPoint,
                'users/account/auctions/drafts',
                { auction_draft: { ...updatedDetails, import_id } },
            );
        }

        return submitRequestWithPromise(
            POST,
            environment.apiEndPoint,
            'users/account/auctions/drafts',
            { auction_draft: updatedDetails },
        );
    },

    downloadPublicNotice({ auctionId, type = 'pdf' }: { auctionId: string | number, type: 'pdf' | 'image' }): Promise<string> {
        const endpoint = 'public-notices/download';
        return submitRequestWithPromise(GET, environment.apiEndPoint, endpoint, {
            auctionId,
            type,
        });
    },

    getPublicNotice({ auctionId }: { auctionId: string | number }): Promise<string> {
        const endpoint = 'public-notices/auctions/' + auctionId + '/content';
        return submitRequestWithPromise(GET, environment.apiEndPoint, endpoint, {
            auctionId,
        });
    },
};

export default AuctionService;
